// Smart Daara - Physical school: school year, periods, hours & grading settings
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { physicalAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const minToHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const hhmmToMin = (s) => { const [h, m] = s.split(':').map(Number); return h * 60 + (m || 0); };

export default function AcademicSettings() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [years, setYears] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [tsConfig, setTsConfig] = useState(null);
  const [grading, setGrading] = useState(null);
  const [yearForm, setYearForm] = useState({ name: '', start_date: '', end_date: '', period_type: 'semester' });

  const load = async () => {
    const [y, ts, gc] = await Promise.all([
      physicalAPI.listYears(), physicalAPI.getTimeslotConfig(), physicalAPI.getGradingConfig(),
    ]);
    setYears(y.data);
    setTsConfig(ts.data);
    setGrading(gc.data);
    const active = y.data.find((yy) => yy.is_active);
    if (active) setPeriods((await physicalAPI.listPeriods(active.id)).data);
  };
  useEffect(() => { load().catch(() => toast.error(fr ? 'Erreur de chargement' : 'Loading error')); }, []);

  const createYear = async () => {
    if (!yearForm.name || !yearForm.start_date || !yearForm.end_date) {
      toast.error(fr ? 'Renseignez nom et dates' : 'Fill name and dates'); return;
    }
    await physicalAPI.createYear(yearForm);
    toast.success(fr ? 'Année scolaire créée' : 'School year created');
    setYearForm({ name: '', start_date: '', end_date: '', period_type: 'semester' });
    load();
  };

  const savePeriod = async (p) => {
    await physicalAPI.updatePeriod(p.id, { start_date: p.start_date, end_date: p.end_date, status: p.status, weight: p.weight });
    toast.success(fr ? 'Période enregistrée' : 'Period saved');
  };

  const saveTsConfig = async () => {
    try {
      await physicalAPI.updateTimeslotConfig({
        day_start: tsConfig.day_start, day_end: tsConfig.day_end,
        slot_minutes: tsConfig.slot_minutes, days: tsConfig.days,
      });
      toast.success(fr ? 'Horaires enregistrés' : 'Hours saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const saveGrading = async () => {
    await physicalAPI.updateGradingConfig({
      scale_max: grading.scale_max, decimals: grading.decimals,
      pass_average: grading.pass_average, mentions: grading.mentions,
    });
    toast.success(fr ? 'Barème enregistré' : 'Grading saved');
  };

  const dayNames = fr ? ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'] : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-6" data-testid="academic-settings">
      <h1 className="text-2xl font-outfit font-bold text-slate-900">{fr ? 'Année scolaire & paramètres' : 'School year & settings'}</h1>

      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">{fr ? 'Années scolaires' : 'School years'}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-5 gap-3 items-end">
            <div><Label>{fr ? 'Nom' : 'Name'}</Label><Input placeholder="2026-2027" value={yearForm.name} onChange={(e) => setYearForm({ ...yearForm, name: e.target.value })} data-testid="year-name-input" /></div>
            <div><Label>{fr ? 'Début' : 'Start'}</Label><Input type="date" value={yearForm.start_date} onChange={(e) => setYearForm({ ...yearForm, start_date: e.target.value })} data-testid="year-start-input" /></div>
            <div><Label>{fr ? 'Fin' : 'End'}</Label><Input type="date" value={yearForm.end_date} onChange={(e) => setYearForm({ ...yearForm, end_date: e.target.value })} /></div>
            <div>
              <Label>{fr ? 'Découpage' : 'Periods'}</Label>
              <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={yearForm.period_type} onChange={(e) => setYearForm({ ...yearForm, period_type: e.target.value })} data-testid="year-period-type">
                <option value="semester">{fr ? 'Semestres (2)' : 'Semesters (2)'}</option>
                <option value="trimester">{fr ? 'Trimestres (3)' : 'Trimesters (3)'}</option>
              </select>
            </div>
            <Button className="bg-emerald-700" onClick={createYear} data-testid="create-year-btn">{fr ? 'Créer l\'année' : 'Create year'}</Button>
          </div>
          <div className="space-y-2">
            {years.map((y) => (
              <div key={y.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                <span className="font-medium">{y.name} <span className="text-slate-400 text-sm">({y.start_date} → {y.end_date})</span></span>
                {y.is_active
                  ? <Badge className="bg-emerald-100 text-emerald-700">{fr ? 'Active' : 'Active'}</Badge>
                  : <Button size="sm" variant="outline" onClick={async () => { await physicalAPI.updateYear(y.id, { is_active: true }); load(); }}>{fr ? 'Activer' : 'Activate'}</Button>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {periods.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-base">{fr ? 'Périodes (année active)' : 'Periods (active year)'}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {periods.map((p, i) => (
              <div key={p.id} className="grid sm:grid-cols-6 gap-2 items-end border-b border-slate-100 pb-3">
                <div className="font-medium">{p.name}</div>
                <div><Label className="text-xs">{fr ? 'Début' : 'Start'}</Label><Input type="date" value={p.start_date || ''} onChange={(e) => setPeriods(periods.map((x, j) => j === i ? { ...x, start_date: e.target.value } : x))} /></div>
                <div><Label className="text-xs">{fr ? 'Fin' : 'End'}</Label><Input type="date" value={p.end_date || ''} onChange={(e) => setPeriods(periods.map((x, j) => j === i ? { ...x, end_date: e.target.value } : x))} /></div>
                <div><Label className="text-xs">{fr ? 'Poids (moy. annuelle)' : 'Weight'}</Label><Input type="number" step="0.5" value={p.weight} onChange={(e) => setPeriods(periods.map((x, j) => j === i ? { ...x, weight: parseFloat(e.target.value) || 1 } : x))} /></div>
                <div>
                  <Label className="text-xs">{fr ? 'Statut' : 'Status'}</Label>
                  <select className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm" value={p.status} onChange={(e) => setPeriods(periods.map((x, j) => j === i ? { ...x, status: e.target.value } : x))}>
                    <option value="open">{fr ? 'Ouverte' : 'Open'}</option>
                    <option value="closed">{fr ? 'Clôturée (notes verrouillées)' : 'Closed (grades locked)'}</option>
                  </select>
                </div>
                <Button size="sm" variant="outline" onClick={() => savePeriod(p)} data-testid={`save-period-${i}`}>{fr ? 'Enregistrer' : 'Save'}</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {tsConfig && (
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-base">{fr ? 'Horaires de l\'école (emploi du temps)' : 'School hours (timetable)'}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-4 gap-3">
              <div><Label>{fr ? 'Début des cours' : 'Day start'}</Label><Input type="time" value={minToHHMM(tsConfig.day_start)} onChange={(e) => setTsConfig({ ...tsConfig, day_start: hhmmToMin(e.target.value) })} data-testid="day-start-input" /></div>
              <div><Label>{fr ? 'Fin des cours' : 'Day end'}</Label><Input type="time" value={minToHHMM(tsConfig.day_end)} onChange={(e) => setTsConfig({ ...tsConfig, day_end: hhmmToMin(e.target.value) })} data-testid="day-end-input" /></div>
              <div><Label>{fr ? 'Durée d\'un créneau (min)' : 'Slot length (min)'}</Label><Input type="number" value={tsConfig.slot_minutes} onChange={(e) => setTsConfig({ ...tsConfig, slot_minutes: parseInt(e.target.value) || 60 })} /></div>
              <div>
                <Label>{fr ? 'Jours ouvrés' : 'Working days'}</Label>
                <div className="flex gap-1 mt-2">
                  {dayNames.map((d, i) => (
                    <button key={i} type="button"
                      className={`px-2 py-1 rounded text-xs border ${tsConfig.days.includes(i) ? 'bg-emerald-700 text-white border-emerald-700' : 'border-slate-200 text-slate-500'}`}
                      onClick={() => setTsConfig({ ...tsConfig, days: tsConfig.days.includes(i) ? tsConfig.days.filter((x) => x !== i) : [...tsConfig.days, i].sort() })}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <Button className="bg-emerald-700" onClick={saveTsConfig} data-testid="save-hours-btn">{fr ? 'Enregistrer les horaires' : 'Save hours'}</Button>
          </CardContent>
        </Card>
      )}

      {grading && (
        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-base">{fr ? 'Barème & mentions' : 'Grading scale & honors'}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>{fr ? 'Note maximale' : 'Max grade'}</Label><Input type="number" value={grading.scale_max} onChange={(e) => setGrading({ ...grading, scale_max: parseFloat(e.target.value) || 20 })} data-testid="scale-max-input" /></div>
              <div><Label>{fr ? 'Moyenne de passage' : 'Pass average'}</Label><Input type="number" value={grading.pass_average} onChange={(e) => setGrading({ ...grading, pass_average: parseFloat(e.target.value) || 10 })} /></div>
              <div><Label>{fr ? 'Décimales' : 'Decimals'}</Label><Input type="number" value={grading.decimals} onChange={(e) => setGrading({ ...grading, decimals: parseInt(e.target.value) || 2 })} /></div>
            </div>
            <div className="space-y-2">
              <Label>{fr ? 'Mentions (seuil minimum)' : 'Honors (minimum threshold)'}</Label>
              {grading.mentions.map((m, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input className="flex-1" value={m.label} onChange={(e) => setGrading({ ...grading, mentions: grading.mentions.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })} />
                  <Input className="w-24" type="number" value={m.min} onChange={(e) => setGrading({ ...grading, mentions: grading.mentions.map((x, j) => j === i ? { ...x, min: parseFloat(e.target.value) || 0 } : x) })} />
                </div>
              ))}
            </div>
            <Button className="bg-emerald-700" onClick={saveGrading} data-testid="save-grading-btn">{fr ? 'Enregistrer le barème' : 'Save grading'}</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
